import React, { useEffect, useState } from 'react';

const COLORS = [
  '#05E6C5', // mint
  '#79F1E0', // mint-light
  '#00C1C1', // teal
  '#FFA672', // apricot
  '#FFCBA8', // apricot-light
  '#B2F7ED', // mint-100
];

const PARTICLE_COUNT = 64;
const ANIMATION_MS = 3500; // a touch longer than the keyframe so trailing particles finish gracefully
const MESSAGE_MS = 2800; // matches @keyframes tt-celebrate-pop

/**
 * Confetti shower + one-shot celebration message across the full viewport.
 * Re-fires whenever `trigger` changes to a new truthy value — callers
 * should bump a counter rather than passing a boolean. `message` (optional)
 * is shown in a small floating pill at the top-center for ~2.8s.
 *
 * Renders nothing while idle and unmounts after the animation so it adds
 * zero ongoing cost.
 */
export function FullScreenConfetti({ trigger, message }) {
  const [particles, setParticles] = useState([]);
  const [activeMessage, setActiveMessage] = useState(null);

  useEffect(() => {
    if (!trigger) return undefined;
    const next = Array.from({ length: PARTICLE_COUNT }).map((_, i) => ({
      id: `${trigger}-${i}`,
      left: Math.random() * 100,
      dx: (Math.random() - 0.5) * 320,
      delay: Math.random() * 0.5,
      rot: (Math.random() * 1080 - 540) | 0,
      color: COLORS[i % COLORS.length],
    }));
    setParticles(next);
    if (message) setActiveMessage({ id: trigger, text: message });
    const tParticles = setTimeout(() => setParticles([]), ANIMATION_MS);
    const tMsg = setTimeout(() => setActiveMessage(null), MESSAGE_MS);
    return () => {
      clearTimeout(tParticles);
      clearTimeout(tMsg);
    };
  }, [trigger, message]);

  if (particles.length === 0 && !activeMessage) return null;

  return (
    <>
      {particles.length > 0 && (
        <div className="tt-confetti-full" aria-hidden="true">
          {particles.map((p) => (
            <span
              key={p.id}
              style={{
                left: `${p.left}%`,
                background: p.color,
                '--dx': `${p.dx}px`,
                '--rot': `${p.rot}deg`,
                animationDelay: `${p.delay}s`,
              }}
            />
          ))}
        </div>
      )}
      {activeMessage && (
        <div
          key={activeMessage.id}
          className="tt-celebrate-msg"
          role="status"
        >
          {activeMessage.text}
        </div>
      )}
    </>
  );
}
